import Router, { useRouter } from 'next/router';
import React from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebouncedCallback<TArgs extends any[]>(callback: (...args: TArgs) => void, wait: number) {
  // Use a ref to store the timeout between renders
  // and prevent changes to it from causing re-renders
  const timeout = React.useRef<string | number | NodeJS.Timeout | undefined>(undefined);

  // Cleanup timeout on unmount to prevent memory leaks
  React.useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);

  return React.useCallback(
    (...args: TArgs) => {
      const later = () => {
        clearTimeout(timeout.current);
        callback(...args);
      };

      clearTimeout(timeout.current);

      timeout.current = setTimeout(later, wait);
    },
    [callback, wait],
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebouncedCallbackWithConfirm<TArgs extends any[]>(
  callbackAsync: (...args: TArgs) => Promise<void>,
  wait: number,
) {
  const [isInProgress, setIsInProgress] = React.useState(false);
  const { asPath: pathname } = useRouter();
  // Use a ref to store the timeout between renders
  // and prevent changes to it from causing re-renders
  const timeout = React.useRef<string | number | NodeJS.Timeout | undefined>(undefined);

  React.useEffect(() => {
    const confirmationMessage = 'Changes are still being saved. Are you sure you want to leave?';

    const beforeRouteHandler = (url: string) => {
      if (isInProgress && pathname !== url && !confirm(confirmationMessage)) {
        Router.events.emit('routeChangeError');
        void Router.replace(pathname); // hack to keep the current url in the browser history
        throw new Error(`Route change aborted`); // safe to ignore this error
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isInProgress) {
        const message = confirmationMessage;
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    Router.events.on('routeChangeStart', beforeRouteHandler);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      Router.events.off('routeChangeStart', beforeRouteHandler);
    };
  }, [isInProgress, pathname]);

  // Cleanup timeout and reset state on unmount
  React.useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      setIsInProgress(false);
    };
  }, []);

  const debouncedFn = React.useCallback(
    (...args: TArgs) => {
      setIsInProgress(true);

      const later = async () => {
        try {
          clearTimeout(timeout.current);
          await callbackAsync(...args);
        } finally {
          setIsInProgress(false);
        }
      };

      clearTimeout(timeout.current);

      timeout.current = setTimeout(() => {
        later().catch(() => {
          // Error handling is done in the finally block above
          // This catch is just to prevent unhandled promise rejections
        });
      }, wait);
    },
    [callbackAsync, wait],
  );

  return debouncedFn;
}
export function useAsyncDebouncedCallback<TArgs extends unknown[]>(
  callbackAsync: (...args: TArgs) => Promise<void>,
  wait: number,
) {
  // Use a ref to store the timeout between renders
  // and prevent changes to it from causing re-renders
  const timeout = React.useRef<string | number | NodeJS.Timeout | undefined>(undefined);

  // Cleanup timeout on unmount to prevent memory leaks
  React.useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);

  const debouncedFn = React.useCallback(
    (...args: TArgs) => {
      const later = async () => {
        clearTimeout(timeout.current);
        await callbackAsync(...args);
      };

      clearTimeout(timeout.current);

      timeout.current = setTimeout(() => {
        later().catch(() => {
          // do nothing, it's on component to handle errors
          // added here just for typescript
        });
      }, wait);
    },
    [callbackAsync, wait],
  );

  return debouncedFn;
}
